import { useTab, useTabList, useTabPanel, AriaTabListOptions, AriaTabPanelProps } from 'react-aria';
import { Item, useTabListState, TabListState, Node, Key, Collection } from 'react-stately';
import { useRef, ReactNode, useState, useEffect } from 'react';
import { NumericBadge, InputDropdown } from '../molecules';
import cx from 'classix';

export interface TabItem {
  id: string;
  content: ReactNode;
  actionCount?: number;
  onTabClick?: (key: Key | string) => void;
}

interface TabsProps extends AriaTabListOptions<TabItem> {
  children?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  fullwidth?: boolean;
  variant?: 'default' | 'side-tabs';
}

const AriaTabs = (props: TabsProps) => {
  const state = useTabListState(props);
  const ref = useRef(null);
  const { tabListProps } = useTabList(props, state, ref);
  const { variant = 'default' } = props;

  // we use an entirely different component for the tab list if mobile
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize(); // Set initial state
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className={cx(variant === 'side-tabs' && 'flex')}>
      <div
        className={cx(
          !isMobile && variant !== 'side-tabs' && 'border-b border-gray-100 flex flex-wrap',
          props.orientation === 'vertical' ? 'flex-col' : 'flex-row',
          props.orientation === 'horizontal' && 'gap-x-4',
          props.fullwidth && 'w-full justify-center',
        )}
        {...tabListProps}
        ref={ref}
      >
        <AriaTabListParent
          items={state.collection}
          state={state}
          fullwidth={props.fullwidth}
          isMobile={isMobile}
        />
      </div>
      <AriaTabPanel key={state.selectedItem?.key} state={state} />
    </div>
  );
};

const AriaTabListParent = ({
  items,
  state,
  isMobile,
  fullwidth = false,
}: {
  items: Collection<Node<TabItem>>;
  state: TabListState<TabItem>;
  isMobile: boolean;
  fullwidth?: boolean;
}) => {
  return !isMobile ?
      <>
        {[...items].map((item) => (
          <AriaTabButton key={item.key} item={item} state={state} fullWidth={fullwidth} />
        ))}
      </>
    : <>
        <AriaTabDropDown items={items} state={state} />
      </>;
};

const AriaTabDropDown = ({
  items,
  state,
}: {
  items: Collection<Node<TabItem>>;
  state: TabListState<TabItem>;
  fullWidth?: boolean;
}) => {
  // hacky way to pass the tabState's fallback to first item to the input dropdown
  // otherwise only works if we explicitly pass defaultSelectedKey prop into Tabs
  const defaultSelectedKey = state.selectedKey ?? [...items][0].key;
  return (
    <InputDropdown
      items={[...items].map((item) => ({
        text: item.key as string,
        textValue: item.key,
      }))}
      type='default'
      onSelectionChange={(key) => state.setSelectedKey(key)}
      defaultSelectedKey={defaultSelectedKey as string}
    />
  );
};

const AriaTabButton = ({
  item,
  state,
  fullWidth,
}: {
  item: Node<TabItem>;
  state: TabListState<TabItem>;
  fullWidth?: boolean;
}) => {
  const { key, rendered, props } = item;
  const ref = useRef(null);
  const { tabProps } = useTab({ key }, state, ref);
  return (
    <button
      {...tabProps}
      ref={ref}
      onClick={props.onTabClick}
      className={cx(
        'px-1 pb-3 h-9 font-semibold flex items-center justify-center gap-2 outline-none',
        fullWidth && 'min-w-60',
        state.selectedKey === key ?
          'border-b-2 border-brand-600 text-brand-500'
        : 'text-gray-400 hover:border-b-2 hover:border-brand-600 hover:text-brand-500 focus:border-brand-600 focus:text-brand-500 focus:border-b-2',
      )}
    >
      {rendered}
      {props.actionCount && <NumericBadge size='sm' color='error' number={props.actionCount} />}
    </button>
  );
};

interface TabPanelProps extends AriaTabPanelProps {
  state: TabListState<TabItem>;
}

const AriaTabPanel = ({ state, ...props }: TabPanelProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { tabPanelProps } = useTabPanel(props, state, ref);
  return (
    <div {...tabPanelProps} ref={ref}>
      {state.selectedItem?.props.children}
    </div>
  );
};

interface MWTabsProps {
  title: string;
  items: TabItem[];
  defaultSelectedKey?: string;
  fullWidth?: boolean;
  orientation?: 'horizontal' | 'vertical';
  onSelectionChange?: (key: Key) => void;
  variant?: 'default' | 'side-tabs';
}

export const Tabs = (props: MWTabsProps) => {
  const {
    title,
    items,
    defaultSelectedKey,
    onSelectionChange,
    fullWidth = false,
    orientation = 'horizontal',
    variant = 'default',
  } = props;

  return (
    <AriaTabs
      items={items}
      aria-label={title}
      defaultSelectedKey={defaultSelectedKey}
      onSelectionChange={onSelectionChange}
      orientation={orientation}
      fullwidth={fullWidth}
      keyboardActivation='manual'
      variant={variant}
    >
      {items.map((item) => (
        <Item key={item.id} title={item.id} {...item}>
          {item.content}
        </Item>
      ))}
    </AriaTabs>
  );
};
